import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../components/Layout/AppLayout';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';

const COMPANY_TYPES = [
  { value: 'company',     label: 'Gazdasági társaság (Kft., Bt., Rt. stb.)' },
  { value: 'sole_trader', label: 'Egyéni vállalkozó (Ev.)' },
];

export default function AddCompanyPage() {
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const [taxId, setTaxId]               = useState('');
  const [companyName, setCompanyName]   = useState('');
  const [companyType, setCompanyType]   = useState('');
  // Whether this taxId belongs to a new (not yet registered) company.
  // null = not yet checked, true = new, false = existing
  const [, setIsNew]                    = useState<boolean | null>(null);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState('');

  // The form has two stages:
  // Stage 1: user enters taxId and we check if it's a known company.
  //          (We optimistically show the extra fields so the form can be
  //           submitted in one go; they stay hidden if the company exists.)
  // We submit everything in one request and the backend decides.

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await api.companies.add({
        taxId: taxId.trim(),
        companyName: companyName.trim() || undefined,
        companyType: companyType || undefined,
      });
      setIsNew(res.isNew);
      // Refresh the auth context so the new companyId is reflected in the UI.
      if (refresh) await refresh();
      // Navigate to prequalification for the newly active company.
      navigate('/prequalification', {
        state: {
          message: res.isNew
            ? `„${res.companyName}" sikeresen létrehozva. Most ebben a cégkontextusban dolgozik.`
            : `Sikeresen csatlakozott: „${res.companyName}". Most ebben a cégkontextusban dolgozik.`,
        },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ismeretlen hiba.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <AppLayout>
      <div style={{ padding: '32px 40px', maxWidth: 2000 }}>
        <div style={{ marginBottom: 28 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--secondary-foreground)', marginBottom: 6 }}>
            FIÓK
          </div>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 700, color: 'var(--primary)' }}>Cég hozzáadása</h1>
          <p style={{ margin: '6px 0 0', fontSize: 14, color: 'var(--muted-foreground)' }}>
            Adjon meg egy adószámot. Ha a cég már regisztrált a rendszerben, automatikusan csatlakozik hozzá.
            Ha még nem, meg kell adnia a cég nevét és formáját is.
          </p>
        </div>

        {error && (
          <div className="alert alert-destructive" style={{ marginBottom: 20 }}>
            <span>⚠</span>
            <div><strong>Hiba</strong><p>{error}</p></div>
          </div>
        )}

        <div style={{
          background: '#fff', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)', padding: '32px 36px',
          boxShadow: 'var(--shadow-md)',
        }}>
          <form onSubmit={(e) => void handleSubmit(e)}>
            <div className="field">
              <label htmlFor="taxId">Adószám (első 8 számjegy) <span className="req">*</span></label>
              <input
                id="taxId" type="text" autoComplete="off"
                value={taxId} onChange={(e) => { setTaxId(e.target.value); setIsNew(null); }}
                placeholder="12345678-1-01" required
              />
              <span style={{ fontSize: 12, color: 'var(--muted-foreground)', marginTop: 4, display: 'block' }}>
                Ha ez az adószám már szerepel a rendszerben, automatikusan csatlakozik a céghez.
              </span>
            </div>

            {/* Extra fields — shown always so the user can fill them in case
                the company is new.  The backend ignores them for existing companies. */}
            <div style={{
              borderTop: '1px solid var(--border)', paddingTop: 20, marginTop: 4,
            }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted-foreground)', marginBottom: 14, letterSpacing: '.08em', textTransform: 'uppercase' }}>
                Új cég esetén kötelező adatok
              </div>

              <div className="field">
                <label htmlFor="companyName">Vállalkozás neve</label>
                <input
                  id="companyName" type="text"
                  value={companyName} onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="Példa Kft."
                />
              </div>

              <div className="field" style={{ marginBottom: 24 }}>
                <label htmlFor="companyType">Cégforma</label>
                <select
                  id="companyType"
                  value={companyType} onChange={(e) => setCompanyType(e.target.value)}
                >
                  <option value="">— válasszon —</option>
                  {COMPANY_TYPES.map(({ value, label }) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading || !taxId.trim()}
              >
                {loading ? 'Feldolgozás…' : 'Cég hozzáadása →'}
              </button>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => navigate(-1)}
              >
                Mégsem
              </button>
            </div>
          </form>
        </div>
      </div>
    </AppLayout>
  );
}
